import { useCallback, useEffect, useRef, useState } from 'react';
import React from 'react';
import { Geometries } from './Geometries';
import { useFloorPlanDispatch, useFloorPlanState } from '../../../../../../../stateProviders/FloorPlanStateProvider';
import { useLocation } from 'react-router-dom';

// DrawSpace is a component that allows the user to draw shapes on the screen by creating a series of points
//This component also handles the logic for panning, zooming, and snapping the corners of the shape to straight lines
//The only geometry-related state generated by this component is the corners of the shape
//The functionalities associated to created lines and shapes are handled by the Geometries component
export const DrawSpace = () => {

    const svgRef = useRef<any>(null);
    const floorPlanState = useFloorPlanState()
    const floorPlanDispatch = useFloorPlanDispatch();
    const [dragStart, setDragStart] = useState<{x:number,y:number}>({x:0,y:0});
    const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null);
    const [initialPinchMidpoint, setInitialPinchMidpoint] = useState<{x:number,y:number}>({x:0,y:0});
    const location = useLocation();

    useEffect(() => {
        const handleScroll = (event:Event) => {
            if (document.activeElement === svgRef.current) {
                event.preventDefault();
                event.stopPropagation(); // Stop the scroll event from bubbling
            }
        };
        // Add the scroll event listener
        window.addEventListener('wheel', handleScroll, { passive: false });
        window.addEventListener('touchmove', handleScroll, { passive: false });

        return () => {
            // Cleanup event listener
            window.removeEventListener('wheel', handleScroll);
            window.removeEventListener('touchmove', handleScroll);
        };
    }, [svgRef.current]);
    
    //returns the x and y of the mouse relative to the SVG drawspace
    const getRelativeCoordinates = useCallback( (e:React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>) => {
        const svg = svgRef.current;
        const rect = svg.getBoundingClientRect(); // Get SVG position relative to the viewport
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        return {
          x: clientX - rect.left,
          y: clientY - rect.top
        };
    },[svgRef.current]);

    const getDistance = useCallback ( (touch1: React.Touch, touch2: React.Touch): number => {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    },[]);

    const getMidpoint = useCallback ((touch1: React.Touch, touch2: React.Touch) => {
        return {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2,
        };
    },[]);

    //updates the coordinates of where the mouse was last held down
    const handleMouseDown = useCallback( (e: React.MouseEvent<SVGSVGElement>) =>  {
        if(floorPlanState.drawingShape)return
        const start = getRelativeCoordinates(e); // Get coordinates relative to the SVG
        setDragStart(start);
    },[floorPlanState.drawingShape,getRelativeCoordinates]);

    const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
        if (dragStart.x === 0 && dragStart.y === 0) return; // Only pan when the mouse is down
        if(floorPlanState.drawingShape)return // do not allow pan if drawing a shape
        const current = getRelativeCoordinates(e); // Get coordinates relative to the SVG
        const dx = current.x - dragStart.x;
        const dy = current.y - dragStart.y;
        floorPlanDispatch({
            panOffset:{
                x:floorPlanState.panOffset.x + dx, 
                y:floorPlanState.panOffset.y + dy
            }
        })
        setDragStart(current); // Update the start position for the next move event
    },[dragStart,getRelativeCoordinates,floorPlanState.drawingShape,floorPlanState.panOffset]);

    //reset clicked mouse position when mouse click is released
    const handleMouseUp = useCallback( () => {
        if(floorPlanState.drawingShape)return
        setDragStart({ x: 0, y: 0 });
    },[floorPlanState.drawingShape]);

    //touch events, allow zooming with pinch
    const handleTouchStart = useCallback ( (e: React.TouchEvent<SVGSVGElement>) => {
        //e.preventDefault();
        if (e.touches.length === 1) {
            handleMouseDown(e as unknown as React.MouseEvent<SVGSVGElement>); // Cast touch event to mouse event
        } else if (e.touches.length === 2) {
            const distance = getDistance(e.touches[0], e.touches[1]);
            setInitialPinchDistance(distance);
        }
    },[handleMouseDown,getDistance]);

    const handleTouchMove = useCallback( (e: React.TouchEvent<SVGSVGElement>) => {
        if (e.touches.length === 1) {
            handleMouseMove(e as unknown as React.MouseEvent<SVGSVGElement>); // Cast touch event to mouse event
        } else if (e.touches.length === 2 && initialPinchDistance !== null && initialPinchMidpoint) {
            const currentDistance = getDistance(e.touches[0], e.touches[1]);
            const zoomFactor = currentDistance / initialPinchDistance;

            // Calculate new zoom level
            floorPlanDispatch({zoomLevel:floorPlanState.zoomLevel * zoomFactor})

            // Get current midpoint for new pan offset adjustment
            const newMidpoint = getMidpoint(e.touches[0], e.touches[1]);
            const dx = newMidpoint.x - initialPinchMidpoint.x;
            const dy = newMidpoint.y - initialPinchMidpoint.y;
            
            floorPlanDispatch({
                panOffset:{
                    x:floorPlanState.panOffset.x + dx,
                    y:floorPlanState.panOffset.y + dy
                }
            })

            // Update initial distance and midpoint for next move
            setInitialPinchDistance(currentDistance);
            setInitialPinchMidpoint(newMidpoint);
        }
    },[handleMouseMove,initialPinchDistance,initialPinchMidpoint,getDistance,getMidpoint,floorPlanState.zoomLevel,floorPlanState.panOffset]);

    //reset pinch distance when touch is released
    const handleTouchEnd = () => {
        handleMouseUp();
        setInitialPinchDistance(null); // Reset distance on touch end
    };

    //zoom in and out with mouse wheel
    const handleWheel = useCallback( (e: React.WheelEvent<SVGSVGElement>) => {
        
        if(svgRef.current !== document.activeElement){
            return
        }
        
        //do not allow zooming if drawing a shape
        if (floorPlanState.drawingShape) return;
        //e.preventDefault();
        const zoomFactor = 1.1; // Zoom scale factor - how much to zoom per scroll
        const boundingRect = svgRef.current.getBoundingClientRect();
        // Get the mouse position relative to the SVG
        const mouseX = (e.clientX - boundingRect.left - floorPlanState.panOffset.x) / floorPlanState.zoomLevel;
        const mouseY = (e.clientY - boundingRect.top - floorPlanState.panOffset.y) / floorPlanState.zoomLevel;
        // Determine new zoom level
        const newZoomLevel = e.deltaY < 0 ? floorPlanState.zoomLevel * zoomFactor : floorPlanState.zoomLevel / zoomFactor;
        // Update the pan offset to keep the zoom centered on the mouse

        floorPlanDispatch({
            zoomLevel:newZoomLevel,
            panOffset:{
                x: e.clientX - boundingRect.left - mouseX * newZoomLevel,
                y: e.clientY - boundingRect.top - mouseY * newZoomLevel,
            }
        })
    },[floorPlanState.drawingShape,floorPlanState.panOffset,svgRef.current,floorPlanState.zoomLevel]);

    //when drawing is stopped, snap the corners x or y to that value of the previous corner, whichever has a lower difference
    //this will turn the shape into straight lines
    useEffect( () => {
        if(floorPlanState.drawingShape)return
        //since we don't do diagonal lines, we need at least 4 corners to make a shape
        if(floorPlanState.corners.length < 4) return

        const updatedCorners:{x:number,y:number}[] = []
        floorPlanState.corners.forEach((corner, index) => {
            if(index === 0) return
            let prevCorner = {x:0, y:0};
            //if first corner, snap to it
            if(index === 1){
                prevCorner = floorPlanState.corners[0]
            //else snap to the previous updated corner
            }else{
                prevCorner = updatedCorners[updatedCorners.length-1]
            }
            //find out if x or y should be snapped to
            const xDiff = corner.x - prevCorner.x
            const yDiff = corner.y - prevCorner.y
            //snap to the value with the lowest difference
            if(Math.abs(xDiff) > Math.abs(yDiff)){
                updatedCorners.push({x:corner.x, y:prevCorner.y})
            }else{
                updatedCorners.push({x:prevCorner.x, y:corner.y})
            }
        })
        //add the first corner to the end to close the shape
        updatedCorners.push({x:updatedCorners[0].x, y:updatedCorners[0].y})
        
        floorPlanDispatch({corners:updatedCorners})

    },[floorPlanState.drawingShape])

    useEffect(() => {
        const updateBoundingRect = () => {
          if (svgRef.current) {
            floorPlanDispatch({ drawspaceBoundingRect: svgRef.current.getBoundingClientRect() });
          }
        };
    
        // Create a ResizeObserver to watch for size changes of the SVG
        const resizeObserver = new ResizeObserver(updateBoundingRect);
    
        // Observe the SVG element
        if (svgRef.current) {
          resizeObserver.observe(svgRef.current);
        }
    
        // Clean up observer on unmount
        return () => {
          resizeObserver.disconnect();
        };
      }, [svgRef.current,location]);
    
    return (
        <svg 
            id="drawspace"
            onClick={(e)=> {
                handleClick(e)
                svgRef.current.focus()
            }} 
            ref={svgRef} 
            className='w-full h-full bg-white min-h-[500px]'
            tabIndex={0}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => {
                handleMouseUp()
                svgRef.current.blur()
            }} // Stop panning if the mouse leaves the SVG
            onWheel={handleWheel} // Capture mouse wheel for zoom

        >
            <g transform={`translate(${floorPlanState.panOffset.x}, ${floorPlanState.panOffset.y}) scale(${floorPlanState.zoomLevel})`}>
                <Geometries/>
            </g>
        </svg>
    )
    function handleClick(e: React.MouseEvent<SVGSVGElement, MouseEvent>) {
        if (!svgRef.current || !floorPlanState.drawingShape) return;
      
        const boundingRect = svgRef.current.getBoundingClientRect();
      
        // Calculate the mouse position relative to the SVG 
        let mouseX = e.clientX - boundingRect.left ;
        let mouseY = e.clientY - boundingRect.top;
        
        // Adjust for pan and zoom
        mouseX = (mouseX - floorPlanState.panOffset.x) / floorPlanState.zoomLevel;
        mouseY = (mouseY - floorPlanState.panOffset.y) / floorPlanState.zoomLevel;
      
        // Check if the click is within 5px of an existing corner
        for (let i = 0; i < floorPlanState.corners.length; i++) {
          if (
            Math.abs(floorPlanState.corners[i].x - mouseX) < 10 &&
            Math.abs(floorPlanState.corners[i].y - mouseY) < 10
          ) {
            // Add the corner to the state if the click is near an existing corner
            floorPlanDispatch({
              corners: [
                ...floorPlanState.corners,
                { x: floorPlanState.corners[i].x, y: floorPlanState.corners[i].y },
              ],
            });
            return;
          }
        }
      
        // Add a new corner to the state based on the adjusted mouse coordinates
        floorPlanDispatch({
          corners: [
            ...floorPlanState.corners,
            { x: Math.round(mouseX), y: Math.round(mouseY) },
          ],
        });
      }
}